import { confirmDialog, reportLoadError, notify } from '../utils/notify';
import { parseLocalDate, toDateInputValue } from '../utils/dateUtils';
import React, { useState, useEffect, useContext } from 'react';
import { documentsAPI, employeeAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { CalendarClock, Download, File, FileImage, FileText, Paperclip, Pencil, Save, Trash2, Upload, User } from 'lucide-react';
import './Documents.css';

const Documents = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [uploadForm, setUploadForm] = useState({
    employee_id: '',
    category_id: '',
    title: '',
    description: '',
    expires_at: '',
    notes: ''
  });

  const [filters, setFilters] = useState({
    employee_id: '',
    category_id: '',
    status: 'active'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docsRes, catsRes, empsRes] = await Promise.all([
        documentsAPI.getAll(filters),
        documentsAPI.getCategories(),
        employeeAPI.getAll()
      ]);

      setDocuments(docsRes.data.documents);
      setCategories(catsRes.data.categories);
      setEmployees(empsRes.data.employees.filter(e => e.status === 'active'));
    } catch (error) {
      console.error('Error loading data:', error);
      reportLoadError(error, t('Error loading documents'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      notify(t('Please select a file'), 'warning');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('employee_id', uploadForm.employee_id);
      formData.append('category_id', uploadForm.category_id);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('expires_at', uploadForm.expires_at);
      formData.append('notes', uploadForm.notes);

      await documentsAPI.upload(formData);
      notify(t('Document uploaded successfully!'), 'success');
      setShowUploadModal(false);
      resetUploadForm();
      setSelectedFile(null);
      loadData();
    } catch (error) {
      notify(error.response?.data?.message || t('Error uploading document'), 'error');
    }
  };

  const handleDownload = async (id, fileName) => {
    try {
      const response = await documentsAPI.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      notify(t('Error downloading document'), 'error');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await documentsAPI.update(editingDocument.id, editingDocument);
      notify(t('Document updated!'), 'success');
      setShowEditModal(false);
      setEditingDocument(null);
      loadData();
    } catch (error) {
      notify(t('Error updating document'), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog(t('Delete this document? This cannot be undone.'))) return;
    
    try {
      await documentsAPI.delete(id);
      notify(t('Document deleted'), 'success');
      loadData();
    } catch (error) {
      notify(t('Error deleting document'), 'error');
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      employee_id: '',
      category_id: '',
      title: '',
      description: '',
      expires_at: '',
      notes: ''
    });
  };

  const openEditModal = (doc) => {
    setEditingDocument({
      ...doc,
      expires_at: doc.expires_at ? toDateInputValue(doc.expires_at) : doc.expires_at
    });
    setShowEditModal(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return t('0 Bytes');
    const k = 1024;
    const sizes = [t('Bytes'), 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText aria-hidden="true" />;
    if (fileType?.includes('word') || fileType?.includes('doc')) return <FileText aria-hidden="true" />;
    if (fileType?.includes('image')) return <FileImage aria-hidden="true" />;
    return <File aria-hidden="true" />;
  };

  const canManage = ['super_admin', 'hr_manager', 'finance_manager'].includes(user?.role);

  return (
    <div className="documents-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Document Management')}</h1>
          <p className="page-sub">{t('Manage employee documents and files')}</p>
        </div>
        {canManage && (
          <button onClick={() => setShowUploadModal(true)} className="btn btn--primary">
            <Upload aria-hidden="true" /> {t('Upload Document')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card doc-filters">
        <div className="doc-filter">
          <label className="field-label">{t('Employee')}</label>
          <select className="select"
            value={filters.employee_id}
            onChange={(e) => {
              setFilters({...filters, employee_id: e.target.value});
              setTimeout(loadData, 100);
            }}
          >
            <option value="">{t('All Employees')}</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className="doc-filter">
          <label className="field-label">{t('Category')}</label>
          <select className="select"
            value={filters.category_id}
            onChange={(e) => {
              setFilters({...filters, category_id: e.target.value});
              setTimeout(loadData, 100);
            }}
          >
            <option value="">{t('All Categories')}</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="doc-filter">
          <label className="field-label">{t('Status')}</label>
          <select className="select"
            value={filters.status}
            onChange={(e) => {
              setFilters({...filters, status: e.target.value});
              setTimeout(loadData, 100);
            }}
          >
            <option value="">{t('All Status')}</option>
            <option value="active">{t('Active')}</option>
            <option value="expired">{t('Expired')}</option>
            <option value="archived">{t('Archived')}</option>
          </select>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="doc-content">
        {loading ? (
          <div className="empty">{t('Loading documents...')}</div>
        ) : documents.length === 0 ? (
          <div className="empty">
            <p>{t('No documents found')}</p>
            {canManage && (
              <button onClick={() => setShowUploadModal(true)} className="btn btn--primary">
                {t('Upload First Document')}
              </button>
            )}
          </div>
        ) : (
          <div className="doc-grid">
            {documents.map(doc => (
              <div key={doc.id} className="card doc-card">
                <div className="doc-card__icon">
                  {getFileIcon(doc.file_type)}
                </div>
                
                <div className="doc-card__head">
                  <h3>{doc.title}</h3>
                  <StatusBadge status={doc.status}>
                    {t(doc.status)}
                  </StatusBadge>
                </div>

                <div className="doc-card__info">
                  <p className="doc-card__employee">
                    <User aria-hidden="true" /> {doc.first_name} {doc.last_name}
                  </p>
                  <p className="doc-card__category">
                    {doc.category_icon} {doc.category_name}
                  </p>
                  {doc.description && (
                    <p className="doc-card__desc">{doc.description}</p>
                  )}
                </div>

                <div className="doc-card__meta">
                  <span className="doc-card__size">{formatFileSize(doc.file_size)}</span>
                  <span className="doc-card__date">
                    {new Date(doc.uploaded_at).toLocaleDateString(locale)}
                  </span>
                </div>

                {doc.expires_at && (
                  <div className="doc-card__expires">
                    <CalendarClock aria-hidden="true" /> {t('Expires: {date}', { date: parseLocalDate(doc.expires_at)?.toLocaleDateString(locale) })}
                  </div>
                )}

                <div className="doc-card__actions">
                  <button
                    onClick={() => handleDownload(doc.id, doc.file_name)}
                    className="btn btn--secondary doc-download"
                    title={t('Download')}
                  >
                    <Download aria-hidden="true" /> {t('Download')}
                  </button>
                  {canManage && (
                    <>
                      <button
                        onClick={() => openEditModal(doc)}
                        className="btn btn--secondary btn--icon"
                        title={t('Edit')}
                        aria-label={t('Edit')}
                      >
                        <Pencil aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="btn btn--secondary btn--icon btn--danger-text"
                        title={t('Delete')}
                        aria-label={t('Delete')}
                      >
                        <Trash2 aria-hidden="true" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <Modal open title={t('Upload Document')} onClose={() => { setShowUploadModal(false); resetUploadForm(); setSelectedFile(null); }} size="lg">

            <form onSubmit={handleUpload} className="doc-dialog">
              <div className="doc-field">
                <label className="field-label">{t('Employee *')}</label>
                <select className="select"
                  value={uploadForm.employee_id}
                  onChange={(e) => setUploadForm({...uploadForm, employee_id: e.target.value})}
                  required
                >
                  <option value="">{t('Select employee')}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.position}
                    </option>
                  ))}
                </select>
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Category *')}</label>
                <select className="select"
                  value={uploadForm.category_id}
                  onChange={(e) => setUploadForm({...uploadForm, category_id: e.target.value})}
                  required
                >
                  <option value="">{t('Select category')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Title *')}</label>
                <input className="input"
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  required
                  placeholder={t('e.g., Employment Contract 2025')}
                />
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  rows="3"
                  placeholder={t('Optional description...')}
                />
              </div>

              <div className="doc-field">
                <label className="field-label">{t('File *')}</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  required
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                />
                <small className="field-hint">{t('Allowed: PDF, DOC, DOCX, JPG, PNG, TXT (Max 10MB)')}</small>
                {selectedFile && (
                  <div className="doc-file">
                    <Paperclip aria-hidden="true" /> {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </div>
                )}
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Expiration Date (Optional)')}</label>
                <input className="input"
                  type="date"
                  value={uploadForm.expires_at}
                  onChange={(e) => setUploadForm({...uploadForm, expires_at: e.target.value})}
                />
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Notes')}</label>
                <textarea className="textarea"
                  value={uploadForm.notes}
                  onChange={(e) => setUploadForm({...uploadForm, notes: e.target.value})}
                  rows="2"
                  placeholder={t('Optional notes...')}
                />
              </div>

              <div className="doc-form-actions">
                <button type="button" onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                  setSelectedFile(null);
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  <Upload aria-hidden="true" /> {t('Upload Document')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEditModal && editingDocument && (
        <Modal open title={t('Edit Document')} onClose={() => { setShowEditModal(false); setEditingDocument(null); }} size="md">

            <form onSubmit={handleUpdate} className="doc-dialog">
              <div className="doc-field">
                <label className="field-label">{t('Title *')}</label>
                <input className="input"
                  type="text"
                  value={editingDocument.title}
                  onChange={(e) => setEditingDocument({...editingDocument, title: e.target.value})}
                  required
                />
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={editingDocument.description || ''}
                  onChange={(e) => setEditingDocument({...editingDocument, description: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Expiration Date')}</label>
                <input className="input"
                  type="date"
                  value={editingDocument.expires_at || ''}
                  onChange={(e) => setEditingDocument({...editingDocument, expires_at: e.target.value})}
                />
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Status *')}</label>
                <select className="select"
                  value={editingDocument.status}
                  onChange={(e) => setEditingDocument({...editingDocument, status: e.target.value})}
                  required
                >
                  <option value="active">{t('Active')}</option>
                  <option value="expired">{t('Expired')}</option>
                  <option value="archived">{t('Archived')}</option>
                </select>
              </div>

              <div className="doc-field">
                <label className="field-label">{t('Notes')}</label>
                <textarea className="textarea"
                  value={editingDocument.notes || ''}
                  onChange={(e) => setEditingDocument({...editingDocument, notes: e.target.value})}
                  rows="2"
                />
              </div>

              <div className="doc-form-actions">
                <button type="button" onClick={() => {
                  setShowEditModal(false);
                  setEditingDocument(null);
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  <Save aria-hidden="true" /> {t('Update')}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default Documents;